import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Envelope, SignIn, ArrowLeft } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { LOGO_URL, FLYER_URL } from "../lib/assets";

export default function AdminLogin() {
  const { t } = useLang();
  const { user, login, error } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user.role) navigate("/admin/dashboard");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await login(email, password);
    setLoading(false);
    if (ok) navigate("/admin/dashboard");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-sage" data-testid="admin-login-page">
      {/* left visual */}
      <div className="relative hidden lg:block overflow-hidden">
        <img src={FLYER_URL} alt="Kelsy Housecleaning" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-forest-deep/70" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <img src={LOGO_URL} alt="Kelsy" className="h-16 w-16 rounded-2xl object-cover" />
          <div>
            <h1 className="font-serif text-5xl leading-tight">Espace<br />administrateur</h1>
            <p className="mt-4 text-white/70 max-w-sm">Gérez vos demandes de devis et factures — Kelsy Housecleaning Ottawa.</p>
          </div>
        </div>
      </div>

      {/* form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          <button onClick={() => navigate("/")} className="mb-8 inline-flex items-center gap-2 text-sm font-semibold text-forest/60 hover:text-forest transition-colors" data-testid="back-home">
            <ArrowLeft size={16} weight="bold" /> Accueil
          </button>
          <div className="lg:hidden mb-6 flex items-center gap-3">
            <img src={LOGO_URL} alt="Kelsy" className="h-12 w-12 rounded-xl object-cover" />
            <span className="font-serif text-2xl text-forest">Kelsy</span>
          </div>
          <h2 className="font-serif text-4xl text-forest">{t.admin.loginTitle}</h2>
          <p className="mt-2 text-forest/60">{t.admin.loginSub}</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form">
            <div className="space-y-2">
              <Label htmlFor="email">{t.admin.email}</Label>
              <div className="relative">
                <Envelope size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-forest/40" />
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-xl h-12 pl-10" required data-testid="login-email" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t.admin.password}</Label>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-forest/40" />
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-xl h-12 pl-10" required data-testid="login-password" />
              </div>
            </div>

            {error && <div className="rounded-xl bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive" data-testid="login-error">{error}</div>}

            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-forest px-7 py-4 text-sm font-bold text-white hover:bg-forest-dark transition-colors disabled:opacity-60"
              data-testid="login-submit"
            >
              <SignIn size={18} weight="bold" /> {loading ? t.admin.loggingIn : t.admin.login}
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
